import { useState, useEffect, useMemo, useRef } from 'react';
import { formatPhoneNumber, validatePhoneNumber } from '@/utils/phoneValidation';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BankTransferScreen from '@/components/BankTransferScreen';
import waveLogo from '@/assets/wave-logo.png';
import { processingFeeFor } from '../../supabase/functions/_shared/paymentRules';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Clock, Zap, Calendar, MapPin, Phone, Tag, CreditCard, Banknote, Landmark, Check } from 'lucide-react';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/providers/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useLocationPermission } from '@/hooks/useLocationPermission';
import { calculateDeliveryFeeByLocation, calculateDeliveryFeeFromAddress, getFallbackDeliveryFee, type DeliveryFeeResult } from '@/utils/deliveryFeeCalculation';
import { getKadiKadiSalePrice, getPyfcKitchenSalePrice, calculateCartTotalWithBundles, getTropicanaSalePrice } from '@/utils/salePricing';
import DeliveryPinPicker from '@/components/DeliveryPinPicker';
import { FEATURE_FLAGS, isOrdersTabUser, isCardPaymentsUser } from '@/config/features';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { useBubblesBogo } from '@/hooks/useBubblesBogo';
import { BUBBLES_TROPICS_ID, BUBBLES_BOGO_CODE, calculateBubblesBogoDiscount, countBubblesBogoUnits } from '@/utils/bubblesBogo';


// Restaurant IDs with sale pricing (only for delivery)
const KADI_KADI_ID = '0d29453a-9312-405c-b581-954fe0d2368f';
const PYFC_KITCHEN_ID = 'c625ff68-b35b-4548-9a34-c3a68265b2a0';
const TROPICANA_ID = 'c02c5e24-21ab-494b-a319-7bf46d8d224d';

interface PaymentMethod {
  id: string;
  name: string;
  code: string;
  description: string;
  icon_url?: string;
}

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const submittingRef = useRef(false);
  const showBankTransfer = searchParams.get('payment') === 'bank-transfer';
  const { items, getTotalPrice, clearCart, getRestaurantId, getReferralPostId, orderType, setOrderType, cartType } = useCart();
  const { user } = useAuth();
  const { isAdmin } = useIsAdmin();
  const cardPaymentsEnabled = isCardPaymentsUser(user?.email, isAdmin);

  const { getLiveLocation } = useLocationPermission();
  const [deliveryType, setDeliveryType] = useState<'express' | 'standard' | 'schedule'>('standard');
  const [loading, setLoading] = useState(false);
  const [scheduledTime, setScheduledTime] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryLat, setDeliveryLat] = useState<number | null>(null);
  const [deliveryLng, setDeliveryLng] = useState<number | null>(null);
  const [deliveryDescription, setDeliveryDescription] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const [appliedPromoId, setAppliedPromoId] = useState<string | null>(null);
  const [referralBonusId, setReferralBonusId] = useState<string | null>(null);
  const [referralBonusApplied, setReferralBonusApplied] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('cash');
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [restaurantPrepTime, setRestaurantPrepTime] = useState({ min: 25, max: 35 });
  const [restaurantData, setRestaurantData] = useState<{ address: string } | null>(null);
  const [deliveryFeeResult, setDeliveryFeeResult] = useState<DeliveryFeeResult | null>(null);

  // Bubbles Tropics x GAMBGO — Buy 1 Get 1 Free event
  const { canRedeem: bogoCanRedeem, soldOut: bogoSoldOut, alreadyUsed: bogoAlreadyUsed, visible: bogoVisible, claim: claimBogo, release: releaseBogo, refresh: refreshBogo } = useBubblesBogo();
  const [bogoBasePrices, setBogoBasePrices] = useState<Record<string, number>>({});

  const bogoUnits = countBubblesBogoUnits(items as any);

  useEffect(() => {
    const loadBasePrices = async () => {
      const ids = Array.from(
        new Set(items.filter((i) => i.restaurant_id === BUBBLES_TROPICS_ID).map((i) => i.menu_item_id))
      );
      if (!bogoVisible || ids.length === 0) {
        setBogoBasePrices({});
        return;
      }
      const { data } = await supabase.from('menu_items').select('id, price').in('id', ids);
      const map: Record<string, number> = {};
      (data || []).forEach((row: any) => {
        map[row.id] = Number(row.price);
      });
      setBogoBasePrices(map);
    };
    loadBasePrices();
  }, [items, bogoVisible]);

  const bogoDiscount = bogoCanRedeem && cartType === 'restaurant'
    ? calculateBubblesBogoDiscount(items as any, bogoBasePrices)
    : 0;

  useEffect(() => {
    if (items.length === 0) {
      navigate(cartType === 'marketplace' ? '/marketplace/deals' : '/home');
    }
    
    // Load saved data and payment methods
    loadSavedData();
    loadPaymentMethods();
    
    // Only fetch restaurant-specific data for restaurant orders
    if (cartType === 'restaurant') {
      fetchRestaurantPrepTime();
      fetchRestaurantData();
    } else {
      // For marketplace, use Ramadan promo flat fee of D150
      setDeliveryFeeResult({
        distanceKm: 3,
        baseFee: 150,
        distanceFee: 0,
        totalFee: 150,
        estimatedTime: 25
      });
    }
  }, [items, navigate, user, cartType]);

  // Check for restaurant-specific promos when restaurant data is available (only for restaurant orders)
  useEffect(() => {
    if (cartType === 'restaurant' && restaurantData && deliveryFeeResult) {
      checkRestaurantPromos();
    }
  }, [restaurantData, orderType, deliveryFeeResult, cartType]);

  // Auto-select WAVE when pickup is chosen
  useEffect(() => {
    if (orderType === 'pickup') {
      setSelectedPaymentMethod('wave');
    }
  }, [orderType]);

  // Prefill pin coordinates with the user's current GPS location once
  useEffect(() => {
    if (deliveryLat != null && deliveryLng != null) return;
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setDeliveryLat(pos.coords.latitude);
        setDeliveryLng(pos.coords.longitude);
      },
      () => {},
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 300000 }
    );
  }, []);

  // Auto-apply referral signup bonus (D50 off first order) — silently
  useEffect(() => {
    if (!user || referralBonusApplied || appliedPromoId) return;
    const checkReferralBonus = async () => {
      const { data } = await supabase
        .from('referral_rewards')
        .select('id, discount_amount')
        .eq('user_id', user.id)
        .eq('reward_type', 'signup_bonus')
        .eq('is_used', false)
        .maybeSingle();
      if (data) {
        setDiscount(data.discount_amount);
        setReferralBonusId(data.id);
        setReferralBonusApplied(true);
        toast.success('🎉 Referral welcome bonus applied — D50 off!', { duration: 4000 });
      }
    };
    checkReferralBonus();
  }, [user, referralBonusApplied, appliedPromoId]);

  // Auto-apply promo code from deals carousel
  useEffect(() => {
    const autoApplyPromo = localStorage.getItem('autoApplyPromo');
    if (autoApplyPromo && !appliedPromoId && restaurantData) {
      setPromoCode(autoApplyPromo);
      // Apply the promo code directly
      applyPromoCodeDirectly(autoApplyPromo);
      localStorage.removeItem('autoApplyPromo');
    }
  }, [restaurantData, appliedPromoId]);

  const applyPromoCodeDirectly = async (codeToApply: string) => {
    if (!codeToApply.trim()) return;

    // Bubbles BOGO is calculated separately in the cart/checkout totals
    if (codeToApply.trim().toUpperCase() === BUBBLES_BOGO_CODE) return;

    try {
      const { data, error } = await supabase
        .from('promos')
        .select('*')
        .ilike('code', codeToApply.trim())
        .eq('is_active', true)
        .single();

      if (error || !data) {
        console.error('Promo not found:', error);
        return;
      }

      // Check if promo business_type matches restaurant
      const promoData = data as any;
      if (promoData.business_type && promoData.business_type !== 'restaurant') {
        return;
      }

      // Check if promo is restaurant-specific and matches current restaurant
      const currentRestaurantId = getRestaurantId();
      if (promoData.restaurant_id && promoData.restaurant_id !== currentRestaurantId) {
        return;
      }

      const now = new Date();
      const startDate = new Date(data.start_date);
      const endDate = new Date(data.end_date);

      if (now < startDate || now > endDate) {
        return;
      }

      if (data.usage_limit && data.used_count >= data.usage_limit) {
        return;
      }

      const currentTotal = getTotalPrice();

      if (data.min_order_amount && currentTotal < data.min_order_amount) {
        return;
      }

      let discountAmount = 0;
      let isFreeDelivery = false;
      
      if (data.discount_type === 'free_delivery') {
        isFreeDelivery = true;
        const currentDeliveryFee = deliveryFeeResult?.totalFee || 120;
        discountAmount = orderType === 'delivery' ? currentDeliveryFee : 0;
      } else if (data.discount_type === 'percentage') {
        discountAmount = (currentTotal * data.discount_value) / 100;
        if (data.max_discount) {
          discountAmount = Math.min(discountAmount, data.max_discount);
        }
      } else {
        discountAmount = data.discount_value;
      }

      setDiscount(discountAmount);
      setAppliedPromoId(data.id);
      
      const savedMessage = isFreeDelivery ? '🎉 Free delivery promo applied!' : `🎉 Promo applied! You saved D${discountAmount.toFixed(2)}`;
      toast.success(savedMessage);
    } catch (error) {
      console.error('Error applying promo code:', error);
    }
  };

  const fetchRestaurantPrepTime = async () => {
    const restaurantId = getRestaurantId();
    if (!restaurantId) return;

    try {
      const { data, error } = await supabase
        .from('restaurants')
        .select('avg_prep_time_min, avg_prep_time_max')
        .eq('id', restaurantId)
        .single();

      if (error) throw error;
      
      if (data) {
        setRestaurantPrepTime({
          min: data.avg_prep_time_min || 25,
          max: data.avg_prep_time_max || 35
        });
      }
    } catch (error) {
      console.error('Error fetching restaurant prep time:', error);
    }
  };

  const fetchRestaurantData = async () => {
    const restaurantId = getRestaurantId();
    if (!restaurantId) return;

    try {
      const { data, error } = await supabase
        .from('restaurants')
        .select('address')
        .eq('id', restaurantId)
        .single();

      if (error) throw error;
      
      if (data) {
        setRestaurantData(data);
        // Calculate delivery fee when restaurant data is available
        calculateDeliveryFee(data.address);
      }
    } catch (error) {
      console.error('Error fetching restaurant data:', error);
      // Use fallback delivery fee if restaurant data fetch fails
      setDeliveryFeeResult(getFallbackDeliveryFee());
    }
  };

  const calculateDeliveryFee = async (restaurantAddress: string) => {
    try {
      // Priority 1: Try GPS location
      const location = await getLiveLocation();
      
      if (location && restaurantAddress) {
        const result = calculateDeliveryFeeByLocation(
          location.latitude, 
          location.longitude, 
          restaurantAddress
        );
        if (result) {
          setDeliveryFeeResult(result);
          return;
        }
      }

      // Priority 2: Try address-based calculation from the delivery address field
      if (deliveryAddress && restaurantAddress) {
        const addressResult = await calculateDeliveryFeeFromAddress(deliveryAddress, restaurantAddress);
        if (addressResult) {
          setDeliveryFeeResult(addressResult);
          return;
        }
      }

      // Priority 3: D175 fallback when both GPS and address lookup fail
      setDeliveryFeeResult(getFallbackDeliveryFee());
    } catch (error) {
      console.error('Error calculating delivery fee:', error);
      setDeliveryFeeResult(getFallbackDeliveryFee());
    }
  };

  // Recalculate delivery fee when delivery address changes (debounced)
  useEffect(() => {
    if (cartType !== 'restaurant' || !restaurantData?.address || !deliveryAddress) return;
    
    const timer = setTimeout(() => {
      calculateDeliveryFee(restaurantData.address);
    }, 800);
    
    return () => clearTimeout(timer);
  }, [deliveryAddress]);

  const loadPaymentMethods = async () => {
    try {
      const { data, error } = await supabase
        .from('payment_methods')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setPaymentMethods(data || []);
    } catch (error) {
      console.error('Error loading payment methods:', error);
    }
  };

  const loadSavedData = async () => {
    if (user) {
      try {
        const { data } = await supabase
          .from('saved_locations')
          .select('*')
          .eq('user_id', user.id)
          .eq('is_default', true)
          .single();

        if (data) {
          setDeliveryAddress(data.address_line);
        } else {
          const savedLocation = localStorage.getItem('selectedLocation');
          if (savedLocation) {
            const location = JSON.parse(savedLocation);
            setDeliveryAddress(location.address);
          }
        }

        const { data: profile } = await supabase
          .from('profiles')
          .select('phone, full_name')
          .eq('id', user.id)
          .single();

        if (profile?.phone) {
          setCustomerPhone(profile.phone);
        }

        if (profile?.full_name) {
          setCustomerName(profile.full_name);
        } else {
          setCustomerName(user.email || 'Customer');
        }
      } catch (error) {
        console.error('Error loading saved data:', error);
        setCustomerName(user.email || 'Customer');
      }
    } else {
      const savedLocation = localStorage.getItem('selectedLocation');
      if (savedLocation) {
        const location = JSON.parse(savedLocation);
        setDeliveryAddress(location.address);
      }
      setCustomerName('Guest Customer');
    }
  };

  const checkRestaurantPromos = async () => {
    const restaurantId = getRestaurantId();
    if (!restaurantId || orderType !== 'delivery' || !deliveryFeeResult) return;

    try {
      // Query for active restaurant-specific promos
      const { data, error } = await supabase
        .from('promos')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .eq('is_active', true)
        .maybeSingle();

      if (error) {
        console.error('Error fetching restaurant promo:', error);
        return;
      }

      if (!data) {
        // No restaurant-specific promo found
        return;
      }

      const now = new Date();
      const startDate = new Date(data.start_date);
      const endDate = new Date(data.end_date);

      // Validate promo is within active period
      if (now < startDate || now > endDate) {
        return;
      }

      // Check if promo business_type matches restaurant
      const promoData = data as any;
      if (promoData.business_type && promoData.business_type !== 'restaurant') {
        return;
      }

      // Check if already applied
      if (appliedPromoId === data.id) {
        return;
      }

      // Auto-apply the promo
      if (data.discount_type === 'free_delivery') {
        const discountAmount = deliveryFeeResult.totalFee;
        
        setDiscount(discountAmount);
        setAppliedPromoId(data.id);
        setPromoCode(data.code);
        toast.success(`🎉 Free delivery applied for ${items[0]?.restaurant_name}!`, {
          duration: 5000
        });
      }
    } catch (error) {
      console.error('Error checking restaurant promos:', error);
      // Silently fail - don't show error to user
    }
  };

  const applyPromoCode = async () => {
    if (!promoCode.trim()) {
      toast.error('Please enter a promo code');
      return;
    }

    // Bubbles BOGO is calculated automatically in the cart totals
    if (promoCode.trim().toUpperCase() === BUBBLES_BOGO_CODE) {
      toast.error('This promotion is applied automatically — no code needed');
      return;
    }

    try {
      console.log('Applying promo code:', promoCode.trim());
      
      // Query without case conversion first to see if there are any promos
      const { data: allPromos, error: allPromosError } = await supabase
        .from('promos')
        .select('*')
        .eq('is_active', true);

      console.log('All active promos:', allPromos);

      const { data, error } = await supabase
        .from('promos')
        .select('*')
        .ilike('code', promoCode.trim()) // Use ilike for case-insensitive search
        .eq('is_active', true)
        .single();

      console.log('Promo query result:', { data, error });

      if (error || !data) {
        console.error('Promo not found:', error);
        toast.error('Invalid promo code');
        return;
      }

      // Check if promo business_type matches restaurant
      const promoData = data as any;
      if (promoData.business_type && promoData.business_type !== 'restaurant') {
        toast.error('This promo code is not valid for restaurant orders');
        return;
      }

      // Check if promo is restaurant-specific and matches current restaurant
      const currentRestaurantId = getRestaurantId();
      if (promoData.restaurant_id && promoData.restaurant_id !== currentRestaurantId) {
        toast.error('This promo code is not valid for this restaurant');
        return;
      }

      const now = new Date();
      const startDate = new Date(data.start_date);
      const endDate = new Date(data.end_date);

      console.log('Date validation:', { now, startDate, endDate });

      if (now < startDate) {
        toast.error('Promo code is not yet active');
        return;
      }

      if (now > endDate) {
        toast.error('Promo code has expired');
        return;
      }

      if (data.usage_limit && data.used_count >= data.usage_limit) {
        toast.error('Promo code usage limit reached');
        return;
      }

      const currentTotal = getTotalPrice();
      console.log('Order total for promo validation:', currentTotal);

      if (data.min_order_amount && currentTotal < data.min_order_amount) {
        toast.error(`Minimum order amount is D${data.min_order_amount}`);
        return;
      }

      let discountAmount = 0;
      let isFreeDelivery = false;
      
      // Check if it's a free delivery promo by discount_type
      if (data.discount_type === 'free_delivery') {
        isFreeDelivery = true;
        const currentDeliveryFee = deliveryFeeResult?.totalFee || 120; // Use calculated fee or fallback
        discountAmount = orderType === 'delivery' ? currentDeliveryFee : 0; // Only apply delivery fee discount for delivery orders
      } else if (data.discount_type === 'percentage') {
        discountAmount = (currentTotal * data.discount_value) / 100;
        if (data.max_discount) {
          discountAmount = Math.min(discountAmount, data.max_discount);
        }
      } else {
        discountAmount = data.discount_value;
      }

      console.log('Applied discount:', discountAmount, 'isFreeDelivery:', isFreeDelivery);

      setDiscount(discountAmount);
      setAppliedPromoId(data.id);
      
      const savedMessage = isFreeDelivery ? 'Free delivery applied!' : `Promo code applied! You saved D${discountAmount.toFixed(2)}`;
      toast.success(savedMessage);
    } catch (error) {
      console.error('Error applying promo code:', error);
      toast.error('Error applying promo code');
    }
  };

  // Helper function to get minimum datetime for scheduling (1 hour from now)
  const getMinScheduleTime = () => {
    const now = new Date();
    const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);
    return oneHourLater.toISOString().slice(0, 16);
  };

  // Helper function to validate scheduled time is within business hours (9 AM - 9 PM)
  const validateScheduledTime = (dateTimeString: string) => {
    if (!dateTimeString) return true; // Allow empty for validation
    
    const selectedDate = new Date(dateTimeString);
    const hours = selectedDate.getHours();
    
    if (hours < 9 || hours >= 21) {
      toast.error('Delivery time must be between 9:00 AM and 9:00 PM');
      return false;
    }
    
    return true;
  };

  // Handle scheduled time change with validation
  const handleScheduledTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = e.target.value;
    setScheduledTime(newTime);
    
    if (newTime) {
      validateScheduledTime(newTime);
    }
  };

  // Helper function to get adjusted price based on order type (sale prices only apply for delivery)
  const getAdjustedItemPrice = (item: any): number => {
    const restaurantId = item.restaurant_id;
    
    // For pickup, check if this is a sale restaurant and revert to original price
    if (orderType === 'pickup') {
      // Kadi Kadi: D150 -> D170, D200 -> D220
      if (restaurantId === KADI_KADI_ID) {
        if (item.price === 150) return 170;
        if (item.price === 200) return 220;
      }
      
      // PYFC Kitchen: reverse 10% discount
      if (restaurantId === PYFC_KITCHEN_ID) {
        return Math.round(item.price / 0.9);
      }
      
      // Tropicana: reverse 5% discount
      if (restaurantId === TROPICANA_ID) {
        return Math.round(item.price / 0.95);
      }
    }
    return item.price;
  };

  // Calculate adjusted subtotal based on order type
  const getAdjustedSubtotal = () => {
    return items.reduce((sum, item) => {
      const adjustedPrice = getAdjustedItemPrice(item);
      return sum + (adjustedPrice * item.quantity);
    }, 0);
  };

  // Calculate Frequence bundle discount for marketplace orders
  const bundleTotals = useMemo(() => {
    if (cartType === 'marketplace') {
      return calculateCartTotalWithBundles(items.map(item => ({
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        restaurant_name: item.restaurant_name
      })));
    }
    return { subtotal: 0, bundleDiscount: 0, total: 0, frequenceCount: 0 };
  }, [items, cartType]);

  const deliveryFee = orderType === 'delivery' ? (deliveryFeeResult?.totalFee || 120) : 0;
  const expressFee = deliveryType === 'express' ? 50 : 0;
  const rawSubtotal = getAdjustedSubtotal();
  // Apply bundle discount for marketplace orders
  const subtotal = cartType === 'marketplace' && bundleTotals.bundleDiscount > 0 
    ? rawSubtotal - bundleTotals.bundleDiscount 
    : rawSubtotal;
  const amountBeforeFees = Number(
    Math.max(subtotal + deliveryFee + expressFee - discount - bogoDiscount, 0).toFixed(2)
  );
  const processingFee = processingFeeFor(selectedPaymentMethod, amountBeforeFees);
  const totalAmount = Number((amountBeforeFees + processingFee).toFixed(2));


  const getDeliveryTime = () => {
    switch (deliveryType) {
      case 'express':
        // Express is 10 minutes less than standard minimum
        const expressMin = Math.max(restaurantPrepTime.min - 10, 10);
        const expressMax = restaurantPrepTime.min;
        return `${expressMin}-${expressMax} min`;
      case 'standard':
        // Use restaurant's actual prep time
        return `${restaurantPrepTime.min}-${restaurantPrepTime.max} min`;
      case 'schedule':
        return scheduledTime ? `Scheduled for ${scheduledTime}` : 'Schedule delivery';
      default:
        return `${restaurantPrepTime.min}-${restaurantPrepTime.max} min`;
    }
  };

  // Shared Gambian phone rules: 7-digit legacy numbers, or 9-digit numbers
  // starting with 87, 83, or 86.

  const processPayment = async (orderId: string) => {
    if (selectedPaymentMethod === 'cash') {
      return { success: true, message: 'Cash payment selected' };
    }

    if (selectedPaymentMethod === 'wave') {
      const phoneValidation = validatePhoneNumber(customerPhone);
      if (!phoneValidation.isValid) {
        throw new Error(phoneValidation.message);
      }
    }

    try {
      console.log('Initiating payment with:', {
        amount: totalAmount,
        orderId,
        paymentMethod: selectedPaymentMethod,
        customerPhone: customerPhone
      });

      const { data, error } = await supabase.functions.invoke('modempay-payment', {
        body: {
          amount: totalAmount,
          orderId: orderId,
          paymentMethod: selectedPaymentMethod,
          customerPhone: customerPhone
        }
      });

      console.log('Payment response:', { data, error });

      if (error) {
        console.error('Payment function error:', error);
        // Extract the actual error message from the edge function
        const errorMessage = error.message || 'Payment processing failed';
        throw new Error(errorMessage);
      }

      if (!data || !data.success) {
        const errorMessage = data?.error || 'Payment failed - please try again or use cash payment';
        throw new Error(errorMessage);
      }

      // For Wave payments, redirect to payment link
      if (selectedPaymentMethod === 'wave' && data.checkout_url) {
        // Open payment link in current window
        window.location.href = data.checkout_url;
        
        return {
          success: true,
          paymentId: data.payment_reference,
          message: 'Redirecting to payment...',
          checkout_url: data.checkout_url
        };
      }

      return data;
    } catch (error) {
      console.error('Payment processing error:', error);
      throw error;
    }
  };

  const handlePlaceOrder = async (transferCompleted = false) => {
    if (submittingRef.current) return;
    if ((selectedPaymentMethod === 'card' || selectedPaymentMethod === 'bank_transfer') && (!cardPaymentsEnabled || (selectedPaymentMethod === 'bank_transfer' && !FEATURE_FLAGS.BANK_TRANSFERS_ENABLED))) {
      toast.error('This payment option is not available for your account');
      return;
    }
    if (!user) {
      toast.error('Please login to place an order');
      const returnTo = cartType === 'marketplace' ? '/marketplace/cart' : '/cart';
      navigate(`/auth?redirect=${encodeURIComponent(returnTo)}`);
      return;
    }

    if (orderType === 'delivery' && !deliveryAddress.trim()) {
      toast.error('Please enter a delivery address');
      return;
    }

    if (!customerPhone.trim()) {
      toast.error('Please enter your phone number');
      return;
    }

    if (!customerName.trim()) {
      toast.error('Customer name is required');
      return;
    }

    // Validate phone number for all payment methods
    const phoneValidation = validatePhoneNumber(customerPhone);
    if (!phoneValidation.isValid) {
      toast.error(phoneValidation.message);
      return;
    }

    if (deliveryType === 'schedule' && !scheduledTime) {
      toast.error('Please select a delivery time');
      return;
    }

    // Validate scheduled time is within business hours
    if (deliveryType === 'schedule' && scheduledTime && !validateScheduledTime(scheduledTime)) {
      return;
    }

    // Validate amounts before sending
    if (isNaN(totalAmount) || totalAmount <= 0) {
      toast.error('Invalid order amount');
      return;
    }

    if (isNaN(deliveryFee + expressFee) || (deliveryFee + expressFee) < 0) {
      toast.error('Invalid delivery fee');
      return;
    }

    if (selectedPaymentMethod === 'bank_transfer' && !transferCompleted) {
      setSearchParams({ payment: 'bank-transfer' });
      return;
    }

    submittingRef.current = true;
    setLoading(true);

    try {
      // Get live location for the order if it's a delivery
      let liveLocation: any = null;
      if (orderType === 'delivery') {
        try {
          liveLocation = await getLiveLocation();
          console.log('Got live location for order:', liveLocation);
        } catch (error) {
          console.error('Failed to get live location:', error);
        }
        // Prefer the user-adjusted pin coordinates for tracking
        if (deliveryLat != null && deliveryLng != null) {
          liveLocation = {
            ...(liveLocation || {}),
            latitude: deliveryLat,
            longitude: deliveryLng,
            pin_lat: deliveryLat,
            pin_lng: deliveryLng,
          };
        }
      }

      // Prepare delivery address with description
      let fullDeliveryAddress = orderType === 'delivery' ? deliveryAddress.trim() : 'Restaurant Pickup';
      if (orderType === 'delivery' && deliveryDescription.trim()) {
        fullDeliveryAddress += ` | ${deliveryDescription.trim()}`;
      }

      // Prepare notes for driver with delivery description and timing
      let orderNotes = deliveryType === 'schedule' ? `Scheduled for ${scheduledTime}` : `${deliveryType} ${orderType}`;
      if (orderType === 'delivery' && deliveryDescription.trim()) {
        orderNotes += ` | Delivery Instructions: ${deliveryDescription.trim()}`;
      }

      const requestData: any = {
        customer_id: user.id,
        total_amount: totalAmount,
        delivery_fee: deliveryFee + expressFee,
        delivery_address: fullDeliveryAddress,
        delivery_type: orderType,
        notes: orderNotes,
        paymentMethod: selectedPaymentMethod,
        transfer_completed: transferCompleted,
        customerPhone: customerPhone.trim(),
        customer_name: customerName.trim(),
        // Set initial status to pending (no timer)
        initial_status: 'pending',
        // Include live location if available
        live_location: liveLocation
      };

      // Add cart-type specific data
      if (cartType === 'restaurant') {
        // Group items by restaurant
        const itemsByRestaurant = items.reduce((acc, item) => {
          if (!acc[item.restaurant_id]) {
            acc[item.restaurant_id] = {
              restaurant_id: item.restaurant_id,
              restaurant_name: item.restaurant_name,
              is_primary: !item.is_complementary,
              items: []
            };
          }
          // Use adjusted price based on order type
          const adjustedPrice = getAdjustedItemPrice(item);
          acc[item.restaurant_id].items.push({
            menu_item_id: item.menu_item_id,
            name: item.name,
            quantity: item.quantity,
            price: adjustedPrice,
            customizations: item.customizations || []
          });
          return acc;
        }, {} as Record<string, any>);

        requestData.restaurant_id = getRestaurantId(); // Primary restaurant
        requestData.referral_post_id = getReferralPostId();
        requestData.multi_restaurant = Object.keys(itemsByRestaurant).length > 1;
        requestData.restaurant_orders = Object.values(itemsByRestaurant);
        
        // For backward compatibility, also include flat items list with adjusted prices
        requestData.items = items.map(item => ({
          menu_item_id: item.menu_item_id,
          name: item.name,
          quantity: item.quantity,
          price: getAdjustedItemPrice(item),
          customizations: item.customizations || []
        }));
      } else {
        // Marketplace order - include marketplace-specific data
        requestData.order_type = 'marketplace';
        requestData.items = items.map(item => ({
          marketplace_item_id: item.id,
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          image_url: item.image_url
        }));
      }

      console.log('Placing order with validated data:', requestData);

      // Final validation before sending
      if (!requestData.customer_name) {
        throw new Error('Customer name is required');
      }

      if (requestData.delivery_type === 'delivery' && !requestData.delivery_address.trim()) {
        throw new Error('Delivery address is required for delivery orders');
      }

      const isCardPayment = selectedPaymentMethod === 'card';
      if (isCardPayment) {
        requestData.customer_email = user.email;
        requestData.processing_fee = processingFee;
      }

      // Reserve the Bubbles Buy 1 Get 1 slot BEFORE payment so the 50 cap is never exceeded.
      // Cash orders confirm immediately; Wave/card stay reserved until the payment webhook settles them.
      let bogoRedemptionId: string | null = null;
      if (bogoDiscount > 0) {
        const claimResult = await claimBogo(selectedPaymentMethod === 'cash');
        if (!claimResult?.success || !claimResult.redemption_id) {
          await refreshBogo();
          throw new Error(
            claimResult?.error === 'limit_reached'
              ? 'Sorry, the Buy 1 Get 1 offer just sold out. Please review your total and try again.'
              : claimResult?.error === 'already_used'
                ? 'You have already used the Buy 1 Get 1 offer. Please review your total and try again.'
                : 'Could not apply the Buy 1 Get 1 offer. Please try again.'
          );
        }
        bogoRedemptionId = claimResult.redemption_id;
      }

      let data: any;
      try {
        const res = await supabase.functions.invoke(
          isCardPayment ? 'waychit-payment' : 'modempay-payment',
          { body: requestData }
        );
        console.log('Payment response:', res);
        if (res.error) throw new Error(res.error.message || 'Failed to place order');
        data = res.data;
        if (!data || !data.success) throw new Error(data?.error || 'Order placement failed - please try again');
        if (['wave', 'card'].includes(selectedPaymentMethod) && !data.checkout_url) throw new Error('Payment checkout URL not received');
      } catch (payErr) {
        // Payment service failed — give the Buy 1 Get 1 slot back
        if (bogoRedemptionId) {
          await releaseBogo(bogoRedemptionId);
          refreshBogo();
        }
        throw payErr;
      }

      if (selectedPaymentMethod === 'wave' || selectedPaymentMethod === 'card') {

        // For Wave payments, redirect to checkout URL
        if (data.checkout_url) {
          // Clear cart BEFORE redirecting since order will be created after payment
          clearCart();
          
          toast.success(selectedPaymentMethod === 'card' ? 'Redirecting to secure card payment...' : 'Redirecting to Wave payment...');
          
          // Small delay to ensure cart is cleared and toast is shown
          setTimeout(() => {
            window.location.href = data.checkout_url;
          }, 1000);
          
          return; // Exit here as we're navigating away
        } else {
          throw new Error('Payment checkout URL not received');
        }
      } else {
        // Cash payment - order was created in pending status
        toast.success(selectedPaymentMethod === 'bank_transfer' ? 'Order submitted — awaiting transfer confirmation' : 'Order placed successfully!');
        // Mark referral signup bonus as used
        if (referralBonusId) {
          await supabase
            .from('referral_rewards')
            .update({ is_used: true })
            .eq('id', referralBonusId);
        }
      }

      // Update promo usage if applied (only for cash orders since Wave orders are created after payment)
      if (selectedPaymentMethod === 'cash' && discount > 0 && appliedPromoId) {
        const { data: currentPromo } = await supabase
          .from('promos')
          .select('used_count')
          .eq('id', appliedPromoId)
          .single();

        if (currentPromo) {
          await supabase
            .from('promos')
            .update({ used_count: currentPromo.used_count + 1 })
            .eq('id', appliedPromoId);
        }
      }

      // Order recipient details must never replace the account's identity.

      // Clear cart and navigate to orders (for cash orders only)
      if (selectedPaymentMethod === 'cash' || selectedPaymentMethod === 'bank_transfer') {
        clearCart();
        navigate(isOrdersTabUser(user?.email) ? '/orders' : '/order-history');
      }
    } catch (error: any) {
      console.error('Error placing order:', error);
      
      // Provide user-friendly error messages
      let errorMessage = 'Failed to place order';
      
      if (error.message) {
        errorMessage = error.message;
      }
      
      // Show specific error messages based on the error type
      if (errorMessage.includes('authentication')) {
        toast.error('Payment service authentication error. Please try again or contact support.');
      } else if (errorMessage.includes('Invalid payment request')) {
        toast.error('Please check your details and try again.');
      } else if (errorMessage.includes('Payment service not configured')) {
        toast.error('Payment service is temporarily unavailable. Please try cash payment.');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      submittingRef.current = false;
      setLoading(false);
      setPaymentProcessing(false);
    }
  };

  const getButtonText = () => {
    if (loading) return 'Creating Order...';
    if (paymentProcessing) return 'Processing Payment...';
    
    if (selectedPaymentMethod === 'wave') {
      return `Place order with Wave • D${totalAmount.toFixed(2)}`;
    }

    if (selectedPaymentMethod === 'card') {
      return `Pay by card • D${totalAmount.toFixed(2)}`;
    }

    
    return `Place Order • D${totalAmount.toFixed(2)}`;
  };

  if (items.length === 0) {
    return null;
  }

  if (showBankTransfer && selectedPaymentMethod === 'bank_transfer' && cardPaymentsEnabled) {
    return <BankTransferScreen total={totalAmount} submitting={loading} onBack={() => setSearchParams({})} onComplete={() => handlePlaceOrder(true)} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-6 w-6" />
          </Button>
          <h1 className="text-xl font-semibold">Checkout</h1>
        </div>
      </div>

      <div className="px-4 py-6 space-y-4">
        {/* Order Info */}
        <Card>
          <CardContent className="p-4">
            <h2 className="font-semibold text-lg">
              {cartType === 'marketplace' ? 'Marketplace Order' : items[0]?.restaurant_name}
            </h2>
            {cartType === 'marketplace' && (
              <p className="text-sm text-muted-foreground mt-1">
                {items.length} {items.length === 1 ? 'item' : 'items'}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Order Type Toggle */}
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold mb-3">Order Type</h3>
            <div className="flex gap-4">
              <Button
                variant={orderType === 'delivery' ? 'default' : 'outline'}
                onClick={() => setOrderType('delivery')}
                className={orderType === 'delivery' ? 'bg-red-600 hover:bg-red-700' : ''}
              >
                Delivery
              </Button>
              <Button
                variant={orderType === 'pickup' ? 'default' : 'outline'}
                onClick={() => setOrderType('pickup')}
                className={orderType === 'pickup' ? 'bg-red-600 hover:bg-red-700' : ''}
              >
                Pickup
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Delivery Options (only show for delivery) */}
        {orderType === 'delivery' && (
          <Card>
            <CardContent className="p-4">
              <Tabs value={deliveryType} onValueChange={(value) => setDeliveryType(value as any)}>
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="express" className="flex items-center gap-1">
                    <Zap className="h-4 w-4" />
                    Express
                  </TabsTrigger>
                  <TabsTrigger value="standard" className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    Standard
                  </TabsTrigger>
                  <TabsTrigger value="schedule" className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    Schedule
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="express" className="mt-4">
                  <div className="text-center py-4">
                    <p className="font-medium">Express Delivery</p>
                    <p className="text-sm text-gray-600">{getDeliveryTime()}</p>
                    <p className="text-sm text-green-600">+D50.00 express fee</p>
                  </div>
                </TabsContent>
                
                <TabsContent value="standard" className="mt-4">
                  <div className="text-center py-4">
                    <p className="font-medium">Standard Delivery</p>
                    <p className="text-sm text-gray-600">{getDeliveryTime()}</p>
                    <p className="text-sm text-green-600">No additional fee</p>
                  </div>
                </TabsContent>
                
                <TabsContent value="schedule" className="mt-4">
                  <div className="space-y-3">
                    <p className="font-medium">Schedule Delivery</p>
                    <input
                      type="datetime-local"
                      value={scheduledTime}
                      onChange={handleScheduledTimeChange}
                      min={getMinScheduleTime()}
                      className="w-full p-2 border rounded-md"
                    />
                    <p className="text-sm text-gray-600">
                      Delivery available between 9:00 AM - 9:00 PM
                    </p>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        )}

        {/* Customer Information */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Phone className="h-5 w-5 text-red-600" />
              <h3 className="font-semibold">Customer Information</h3>
            </div>
            <div className="space-y-3">
              <div>
                <Label htmlFor="customerName">Full Name</Label>
                <Input
                  id="customerName"
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Enter your full name"
                  className="w-full"
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  placeholder="e.g., 7123456 or 2207123456"
                  className="w-full"
                  required
                />
                {selectedPaymentMethod === 'wave' && customerPhone && (
                  <div className="text-sm mt-1">
                    {(() => {
                      const validation = validatePhoneNumber(customerPhone);
                      return (
                        <span className={validation.isValid ? 'text-green-600' : 'text-red-600'}>
                          {validation.isValid 
                            ? `✓ Valid: ${formatPhoneNumber(customerPhone)}` 
                            : validation.message
                          }
                        </span>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Delivery Address */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="h-5 w-5 text-red-600" />
              <h3 className="font-semibold">
                {orderType === 'delivery' ? 'Delivery Address' : 'Pickup Location'}
              </h3>
            </div>
            {orderType === 'delivery' ? (
              <div className="space-y-3">
                <div>
                  <Label htmlFor="address">Enter your delivery address</Label>
                  <Input
                    id="address"
                    value={deliveryAddress}
                    onChange={(e) => setDeliveryAddress(e.target.value)}
                    placeholder="Enter your full address"
                    className="w-full"
                  />
                </div>
                <DeliveryPinPicker
                  lat={deliveryLat}
                  lng={deliveryLng}
                  address={deliveryAddress}
                  onChange={({ lat, lng, address }) => {
                    setDeliveryLat(lat);
                    setDeliveryLng(lng);
                    if (address) setDeliveryAddress(address);
                  }}
                />
                <div>
                  <Label htmlFor="description">Delivery Description (Optional)</Label>
                  <Textarea
                    id="description"
                    value={deliveryDescription}
                    onChange={(e) => setDeliveryDescription(e.target.value)}
                    placeholder="e.g., Building color, landmarks, special instructions..."
                    className="w-full"
                    rows={2}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Help the driver find you easily
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/location')}
                  className="text-red-600 border-red-600 hover:bg-red-50"
                >
                  Choose from saved locations
                </Button>
              </div>
            ) : (
              <div className="bg-gray-50 p-3 rounded-md">
                <p className="text-sm font-medium">Pickup from restaurant</p>
                <p className="text-sm text-gray-600">{items[0].restaurant_name}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payment Method Selection */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <CreditCard className="h-5 w-5 text-red-600" />
              <h3 className="font-semibold">Payment Method</h3>
            </div>
            <div role="group" aria-label="Payment method" className="grid grid-cols-2 gap-3">
              {[
                ...paymentMethods.filter(method => ['cash', 'wave'].includes(method.code) && (orderType !== 'pickup' || method.code !== 'cash')).map(method => ({ code: method.code, name: method.code === 'wave' ? 'Wave' : 'Cash', detail: method.code === 'wave' ? 'Mobile wallet' : 'On delivery' })),
                ...(cardPaymentsEnabled ? [{ code: 'card', name: 'Card', detail: '5.3% processing fee' }, ...(FEATURE_FLAGS.BANK_TRANSFERS_ENABLED ? [{ code: 'bank_transfer', name: 'Bank transfer', detail: 'No processing fee' }] : [])] : []),
              ].map(method => (
                <Button key={method.code} variant="outline" aria-pressed={selectedPaymentMethod === method.code} onClick={() => setSelectedPaymentMethod(method.code)} className={`relative h-24 flex-col gap-1 whitespace-normal px-2 text-center ${selectedPaymentMethod === method.code ? 'border-primary bg-accent text-accent-foreground ring-1 ring-primary' : ''}`}>
                  {selectedPaymentMethod === method.code && <Check className="absolute right-2 top-2 h-3 w-3" />}
                  {method.code === 'wave' ? <img src={waveLogo} alt="" className="h-7 w-7 object-contain" /> : method.code === 'cash' ? <Banknote className="!h-7 !w-7 text-primary" /> : method.code === 'card' ? <CreditCard className="!h-7 !w-7 text-primary" /> : <Landmark className="!h-7 !w-7 text-primary" />}
                  <span className="font-semibold">{method.name}</span>
                  <span className="text-xs text-muted-foreground">{method.detail}</span>
                </Button>
              ))}
            </div>

          </CardContent>
        </Card>

        {/* Promo Code */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Tag className="h-5 w-5 text-red-600" />
              <h3 className="font-semibold">Promo Code</h3>
            </div>
            <div className="flex gap-2">
              <Input
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                placeholder="Enter promo code"
                className="flex-1"
              />
              <Button
                variant="outline"
                onClick={applyPromoCode}
                disabled={!promoCode.trim()}
              >
                Apply
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Order Items */}
        <Card>
          <CardHeader>
            <CardTitle>Order Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Group items by restaurant */}
            {(() => {
              const itemsByRestaurant = items.reduce((acc, item) => {
                if (!acc[item.restaurant_id]) {
                  acc[item.restaurant_id] = {
                    restaurantName: item.restaurant_name,
                    items: []
                  };
                }
                acc[item.restaurant_id].items.push(item);
                return acc;
              }, {} as Record<string, { restaurantName: string; items: typeof items }>);

              return Object.entries(itemsByRestaurant).map(([restaurantId, data]) => (
                <div key={restaurantId}>
                  <h4 className="font-semibold text-sm text-muted-foreground mb-2">{data.restaurantName}</h4>
                  <div className="space-y-2">
                    {data.items.map((item) => {
                      const adjustedPrice = getAdjustedItemPrice(item);
                      const showOriginalPrice = orderType === 'delivery' && adjustedPrice < item.price;
                      const showSalePrice = orderType === 'delivery' && (restaurantId === KADI_KADI_ID || restaurantId === PYFC_KITCHEN_ID) && adjustedPrice === item.price;
                      
                      return (
                        <div key={item.id} className="flex justify-between">
                          <span>{item.quantity}x {item.name}</span>
                          <div className="text-right">
                            {/* Show original price struck through when sale applies for delivery */}
                            {orderType === 'delivery' && adjustedPrice < getAdjustedItemPrice({...item, price: item.price}) === false && (restaurantId === KADI_KADI_ID || restaurantId === PYFC_KITCHEN_ID) && (
                              <span className="text-sm text-gray-400 line-through mr-2">
                                D{(item.price * item.quantity).toFixed(2)}
                              </span>
                            )}
                            <span>D{(adjustedPrice * item.quantity).toFixed(2)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ));
            })()}
          </CardContent>
        </Card>

        {/* Order Total */}
        <Card>
          <CardContent className="p-4 space-y-3">
            {/* Show bundle discount for marketplace Frequence items */}
            {cartType === 'marketplace' && bundleTotals.bundleDiscount > 0 && (
              <div className="mb-2 p-2 bg-green-500/10 rounded-lg">
                <p className="text-sm text-green-600 font-medium">
                  🎉 Frequence Bundle Deal: {bundleTotals.frequenceCount} items - Save D{bundleTotals.bundleDiscount.toFixed(0)}!
                </p>
              </div>
            )}
            <div className="flex justify-between">
              <span>Subtotal</span>
              <div className="text-right">
                {cartType === 'marketplace' && bundleTotals.bundleDiscount > 0 && (
                  <span className="text-sm text-muted-foreground line-through mr-2">
                    D{rawSubtotal.toFixed(2)}
                  </span>
                )}
                <span>D{subtotal.toFixed(2)}</span>
              </div>
            </div>
            {orderType === 'delivery' && (
              <div className="flex justify-between">
                <span>Delivery Fee</span>
                <span>D{deliveryFee.toFixed(2)}</span>
              </div>
            )}
            {deliveryType === 'express' && (
              <div className="flex justify-between">
                <span>Express Fee</span>
                <span>D{expressFee.toFixed(2)}</span>
              </div>
            )}
            {discount > 0 && (
              <div className="flex justify-between text-green-600">
                <span>Discount</span>
                <span>-D{discount.toFixed(2)}</span>
              </div>
            )}
            {bogoVisible && bogoUnits > 0 && bogoDiscount === 0 && (
              <div className="p-2 bg-pink-500/10 rounded-lg">
                <p className="text-sm text-pink-600 font-medium">
                  {bogoSoldOut
                    ? '🧋 Buy 1 Get 1 Free is fully claimed for this event.'
                    : bogoAlreadyUsed
                      ? '🧋 You have already used the Buy 1 Get 1 Free offer.'
                      : '🧋 Add one more Bubbles Tropics drink and the cheapest one is free (toppings not included).'}
                </p>
              </div>
            )}
            {bogoDiscount > 0 && (
              <div className="flex justify-between text-green-600">
                <span>Buy 1 Get 1 Free 🧋 (Bubbles Tropics)</span>
                <span>-D{bogoDiscount.toFixed(2)}</span>
              </div>
            )}
            {processingFee > 0 && (
              <div className="flex justify-between">
                <span>Card processing fee (5.3%)</span>
                <span>D{processingFee.toFixed(2)}</span>
              </div>
            )}


            <div className="border-t pt-3">
              <div className="flex justify-between font-semibold text-lg">
                <span>Total</span>
                <span>D{totalAmount.toFixed(2)}</span>
              </div>
              <div className="text-sm text-gray-600 mt-1">
                {getDeliveryTime()}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Place Order Button */}
        <Button 
          className="w-full bg-red-600 hover:bg-red-700 py-6"
          onClick={() => handlePlaceOrder()}
          disabled={loading || paymentProcessing}
        >
          {getButtonText()}
        </Button>
      </div>
    </div>
  );
};

export default CheckoutPage;
